/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { StatusBadge } from '@components/atoms/ui-kit/badge';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue
} from 'react-hook-form';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import { SingleValue } from 'react-select';
import { LabelValueSelectInput } from '@utils/types/shared/label-value-select-input.ts';
import { PlanBo } from '@utils/types/back-office/update-plan-bo.ts';
import PricePlanOverview from '@components/back-office/plans/price-plan-overview';
import Input from '@components/atoms/ui-kit/input.tsx';
import { Check, Shield } from 'lucide-react';

import { usePlanDetailsData } from '@components/back-office/plans/use-plan-details-data';
import { FEATURE_OPTIONS } from '@components/back-office/plans/plan-feature-options';

type Props = {
  register: UseFormRegister<PlanBo>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<PlanBo, any>;
  setValue: UseFormSetValue<PlanBo>;
  errors: FieldErrors<PlanBo>;
  isBatchEnabled: boolean;
  setBatchEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isRealtimeEnabled: boolean;
  setRealtimeEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isAlignmentEnabled: boolean;
  setAlignmentEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isTranslationEnabled: boolean;
  setTranslationEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isTemplateEnabled: boolean;
  setTemplateEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isAutomaticTrainingEnabled: boolean;
  setAutomaticTrainingEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isBurntExportEnabled: boolean;
  setBurntExportEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isAiSummariesEnabled: boolean;
  setAiSummariesEnabled: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function PlanDetails({
  register,
  control,
  setValue,
  errors,
  isBatchEnabled,
  setBatchEnabled,
  isRealtimeEnabled,
  setRealtimeEnabled,
  isAlignmentEnabled,
  setAlignmentEnabled,
  isTranslationEnabled,
  setTranslationEnabled,
  isTemplateEnabled,
  setTemplateEnabled,
  isAutomaticTrainingEnabled,
  setAutomaticTrainingEnabled,
  isBurntExportEnabled,
  setBurntExportEnabled,
  isAiSummariesEnabled,
  setAiSummariesEnabled
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const {
    planBo,
    pricePlans,
    isFairUseEnabled,
    setFairUseEnabled,
    realtimeMaxStreamsInput,
    setRealtimeMaxStreamsInput,
    realtimeLanguagesInput,
    setRealtimeLanguagesInput,
    setSearchText,
    sendExceedanceEmail,
    selectedSttPlanId,
    selectedAlignmentPlanId,
    selectedTranslationPlanId
  } = usePlanDetailsData({
    control,
    setValue,
    setBatchEnabled,
    setRealtimeEnabled,
    setAlignmentEnabled,
    setTranslationEnabled,
    setTemplateEnabled,
    setAutomaticTrainingEnabled
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getTypeString = (val: any) =>
    typeof val === 'string' ? val : (val?.value ?? String(val ?? ''));

  const typeString = getTypeString(planBo?.result?.type);

  const featureStates: Record<string, boolean> = {
    batch_enabled: isBatchEnabled,
    realtime_enabled: isRealtimeEnabled,
    alignment_enabled: isAlignmentEnabled,
    translation_enabled: isTranslationEnabled,
    templates_exported_enabled: isTemplateEnabled,
    automatic_training_enabled: isAutomaticTrainingEnabled,
    allow_burnt_export: isBurntExportEnabled,
    ai_summaries_enabled: isAiSummariesEnabled
  };

  const featureSetters: Record<
    string,
    React.Dispatch<React.SetStateAction<boolean>>
  > = {
    batch_enabled: setBatchEnabled,
    realtime_enabled: setRealtimeEnabled,
    alignment_enabled: setAlignmentEnabled,
    translation_enabled: setTranslationEnabled,
    templates_exported_enabled: setTemplateEnabled,
    automatic_training_enabled: setAutomaticTrainingEnabled,
    allow_burnt_export: setBurntExportEnabled,
    ai_summaries_enabled: setAiSummariesEnabled
  };

  const sectionStyle = {
    backgroundColor: getColour('secondLayer'),
    borderColor: getColour('border')
  };

  return (
    <div className="bg-app py-4 space-y-4 sm:space-y-5 overflow-y-auto">
      {/* Plan Info Header */}
      <div
        className="rounded-2xl border shadow-sm p-4 sm:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
        style={sectionStyle}
      >
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3">
          <StatusBadge
            status={typeString || 'unknown'}
            label={typeString || t('unknown')}
          />
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm">
            {planBo?.result?.sku ? (
              <span className="opacity-80">
                {t('SKU')}:{' '}
                <span className="font-medium">{planBo.result.sku}</span>
              </span>
            ) : null}
            {planBo?.result?.id ? (
              <span className="opacity-80">
                {t('Plan ID')}:{' '}
                <span className="font-medium">#{planBo.result.id}</span>
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Features */}
      <section
        className="rounded-2xl border shadow-sm p-4 sm:p-5"
        style={sectionStyle}
      >
        <div className="mb-3">
          <h3 className="text-sm font-semibold tracking-wide">
            {t('enabled-features')}
          </h3>
          <p className="mt-1 text-xs opacity-80">
            {t('enabled-features-description')}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-1.5">
          {FEATURE_OPTIONS.map(feature => {
            const isEnabled = featureStates[feature.key];
            const Icon = feature.icon;
            return (
              <button
                key={feature.key}
                type="button"
                onClick={() => {
                  const newValue = !isEnabled;
                  featureSetters[feature.key](newValue);
                  setValue(feature.key, newValue);
                }}
                className="relative p-2 rounded-2xl border text-left flex align-middle transition-all"
                style={{
                  borderColor: isEnabled ? feature.color : getColour('border'),
                  backgroundColor: isEnabled
                    ? feature.bgColor
                    : getColour('secondLayer')
                }}
              >
                {isEnabled && (
                  <div
                    className="absolute top-1.5 right-1.5 h-3.5 w-3.5 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: feature.color }}
                  >
                    <Check className="h-2 w-2 text-white" />
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <div
                    className="h-5 w-5 rounded-2xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: feature.bgColor }}
                  >
                    <Icon
                      className="h-3 w-3"
                      style={{ color: feature.color }}
                    />
                  </div>
                  <span
                    className="text-[11px] font-medium truncate"
                    style={{ color: getColour('normalText') }}
                  >
                    {t(feature.label)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Plan Configuration */}
      <section
        className="rounded-2xl border shadow-sm p-4 sm:p-5"
        style={sectionStyle}
      >
        <div className="mb-4">
          <h3 className="text-sm font-semibold tracking-wide">
            {t('plan-configuration')}
          </h3>
        </div>
        <div className="space-y-4">
          <Input
            id="description"
            type="text"
            label={t('description')}
            defaultValue={planBo?.result.description ?? ''}
            autoComplete="description"
            placeholder={t('description')}
            {...register('description', { required: t('description') })}
            onChange={e => setValue('description', e.target.value)}
            error={errors.description?.message}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {getTypeString(planBo?.result?.type) === 'Bundle' && (
              <Input
                id="monthly_cost"
                type="number"
                label={t('monthly-cost')}
                defaultValue={planBo?.result.monthly_cost ?? ''}
                autoComplete="monthly_cost"
                placeholder={t('monthly-cost')}
                {...register('monthly_cost')}
                onChange={e => setValue('monthly_cost', Number(e.target.value))}
                error={errors.monthly_cost?.message}
              />
            )}
            {getTypeString(planBo?.result?.type) === 'Perpetual' && (
              <Input
                id="cost"
                type="number"
                label={t('cost')}
                defaultValue={planBo?.result.cost ?? ''}
                autoComplete="cost"
                placeholder={t('cost')}
                {...register('cost')}
                onChange={e => setValue('cost', Number(e.target.value))}
                error={errors.cost?.message}
              />
            )}
            {getTypeString(planBo?.result?.type) === 'Pay as you Go' && (
              <Input
                id="minimal_invoice"
                type="number"
                label={t('minimal-invoice')}
                defaultValue={planBo?.result.minimal_invoice ?? ''}
                autoComplete="minimal_invoice"
                placeholder={t('minimal-invoice')}
                {...register('minimal_invoice')}
                onChange={e =>
                  setValue('minimal_invoice', Number(e.target.value))
                }
                error={errors.minimal_invoice?.message}
              />
            )}
          </div>
        </div>
      </section>

      {/* STT Pricing */}
      {isBatchEnabled && (
        <section
          className="rounded-2xl border shadow-sm p-4 sm:p-5"
          style={sectionStyle}
        >
          <div className="mb-4">
            <h3 className="text-sm font-semibold tracking-wide">
              {t('stt-pricing')}
            </h3>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="stt_pricing_plan"
                  className="block text-sm font-medium leading-6 truncate max-w-full mb-2"
                >
                  {t('speech-to-text-price-plan')}
                </label>
                <Controller
                  control={control}
                  name="stt_pricing_plan"
                  render={({ field: { onChange, value } }) => (
                    <SelectInput
                      id="stt_pricing_plan"
                      name="stt_pricing_plan"
                      options={
                        pricePlans?.result.map(p => ({
                          label: p.name,
                          value: p.id.toString()
                        })) || []
                      }
                      onInputChange={input => setSearchText(input)}
                      onChange={selected => {
                        onChange(
                          selected
                            ? Number(
                                (selected as SingleValue<LabelValueSelectInput>)
                                  ?.value ?? 0
                              )
                            : null
                        );
                      }}
                      placeholder={t('select')}
                      value={(() => {
                        const numericValue =
                          typeof value === 'object' && value !== null
                            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              (value as any).id
                            : value != null && String(value) !== ''
                              ? Number(value)
                              : null;
                        if (numericValue == null) return null;
                        const matched = pricePlans?.result.find(
                          pl => pl.id === numericValue
                        );
                        const sttPlan = planBo?.result?.stt_pricing_plan;
                        const fallbackName =
                          typeof sttPlan === 'object' && sttPlan !== null
                            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              (sttPlan as any)?.name
                            : undefined;
                        return {
                          label:
                            matched?.name ??
                            fallbackName ??
                            `Plan #${numericValue}`,
                          value: String(numericValue)
                        };
                      })()}
                      isMulti={false}
                      error={errors.stt_pricing_plan?.message}
                    />
                  )}
                />
                <PricePlanOverview
                  pricePlanId={
                    typeof selectedSttPlanId === 'object' &&
                    selectedSttPlanId !== null
                      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (selectedSttPlanId as any).id
                      : (selectedSttPlanId as number | null | undefined)
                  }
                  unitLabel="min"
                />
              </div>
              <Input
                id="media_unit_size"
                type="number"
                label={t('media-unit-size')}
                defaultValue={planBo?.result.media_unit_size ?? ''}
                autoComplete="media_unit_size"
                placeholder={t('media-unit-size')}
                {...register('media_unit_size', {
                  required: t('media-unit-size')
                })}
                onChange={e =>
                  setValue('media_unit_size', Number(e.target.value))
                }
                error={errors.media_unit_size?.message}
              />
              <Input
                id="stt_limit"
                type="number"
                label={
                  <>
                    {t('stt-limit')}
                    <span
                      className="ml-1 text-[10px] font-normal"
                      style={{ color: getColour('inactiveText') }}
                    >
                      ({t('fair-use-soft-cap')})
                    </span>
                  </>
                }
                defaultValue={planBo?.result.stt_limit ?? ''}
                autoComplete="stt_limit"
                placeholder={t('unlimited')}
                {...register('stt_limit', { required: t('stt-limit') })}
                onChange={e => setValue('stt_limit', Number(e.target.value))}
                error={errors.stt_limit?.message}
              />
            </div>
          </div>
        </section>
      )}

      {/* Realtime */}
      {isRealtimeEnabled && (
        <section
          className="rounded-2xl border shadow-sm p-4 sm:p-5"
          style={sectionStyle}
        >
          <div className="mb-4">
            <h3 className="text-sm font-semibold tracking-wide">
              {t('realtime')}
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              id="realtime_max_concurrent_streams"
              type="number"
              label={t('max-concurrent-realtime-streams')}
              value={realtimeMaxStreamsInput}
              placeholder={t('unlimited')}
              onChange={e => {
                const next = e.target.value;
                setRealtimeMaxStreamsInput(next);
                setValue(
                  'realtime_max_concurrent_streams',
                  next === '' || isNaN(Number(next)) ? null : Number(next)
                );
              }}
            />
            <Input
              id="realtime_languages"
              label={t('allowed-realtime-languages')}
              value={realtimeLanguagesInput}
              placeholder={t('realtime-languages-placeholder')}
              onChange={e => {
                const next = e.target.value;
                setRealtimeLanguagesInput(next);
                const langs = next
                  .split(',')
                  .map(s => s.trim().toLowerCase())
                  .filter(Boolean);
                setValue(
                  'realtime_languages',
                  langs.length ? Array.from(new Set(langs)) : null
                );
              }}
            />
          </div>
        </section>
      )}

      {/* AI Summaries */}
      {isAiSummariesEnabled && (
        <section
          className="rounded-2xl border shadow-sm p-4 sm:p-5"
          style={sectionStyle}
        >
          <div className="mb-4">
            <h3 className="text-sm font-semibold tracking-wide">
              {t('ai-summaries')}
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              id="ai_summaries_monthly_limit"
              type="number"
              label={t('monthly-limit')}
              placeholder={t('unlimited')}
              {...register('ai_summaries_monthly_limit', {
                setValueAs: (v: string) =>
                  v === '' || v === null || v === undefined || isNaN(Number(v))
                    ? null
                    : Number(v)
              })}
            />
          </div>
        </section>
      )}

      {/* Alignment Pricing */}
      {isAlignmentEnabled && (
        <section
          className="rounded-2xl border shadow-sm p-4 sm:p-5"
          style={sectionStyle}
        >
          <div className="mb-4">
            <h3 className="text-sm font-semibold tracking-wide">
              {t('alignment-pricing')}
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label
                htmlFor="alignment_pricing_plan"
                className="block text-sm font-medium leading-6 truncate max-w-full mb-2"
              >
                {t('alignment-price-plan')}
              </label>
              <Controller
                control={control}
                name="alignment_pricing_plan"
                render={({ field: { onChange, value } }) => (
                  <SelectInput
                    id="alignment_pricing_plan"
                    name="alignment_pricing_plan"
                    options={
                      pricePlans?.result.map(p => ({
                        label: p.name,
                        value: p.id.toString()
                      })) || []
                    }
                    onChange={selected => {
                      onChange(
                        selected
                          ? Number(
                              (selected as SingleValue<LabelValueSelectInput>)
                                ?.value ?? 0
                            )
                          : null
                      );
                    }}
                    value={(() => {
                      const numericValue =
                        typeof value === 'object' && value !== null
                          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            (value as any).id
                          : value != null && String(value) !== ''
                            ? Number(value)
                            : null;
                      if (numericValue == null) return null;
                      const matched = pricePlans?.result.find(
                        pl => pl.id === numericValue
                      );
                      const alignPlan = planBo?.result?.alignment_pricing_plan;
                      const fallbackName =
                        typeof alignPlan === 'object' && alignPlan !== null
                          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            (alignPlan as any)?.name
                          : undefined;
                      return {
                        label:
                          matched?.name ??
                          fallbackName ??
                          `Plan #${numericValue}`,
                        value: String(numericValue)
                      };
                    })()}
                    isMulti={false}
                    error={errors.alignment_pricing_plan?.message}
                  />
                )}
              />
              <PricePlanOverview
                pricePlanId={
                  typeof selectedAlignmentPlanId === 'object' &&
                  selectedAlignmentPlanId !== null
                    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (selectedAlignmentPlanId as any).id
                    : (selectedAlignmentPlanId as number | null | undefined)
                }
                unitLabel="min"
              />
            </div>
          </div>
        </section>
      )}

      {/* Translation Pricing */}
      {isTranslationEnabled && (
        <section
          className="rounded-2xl border shadow-sm p-4 sm:p-5"
          style={sectionStyle}
        >
          <div className="mb-4">
            <h3 className="text-sm font-semibold tracking-wide">
              {t('translation-pricing')}
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label
                htmlFor="translation_pricing_plan"
                className="block text-sm font-medium leading-6 truncate max-w-full mb-2"
              >
                {t('translation-price-plan')}
              </label>
              <Controller
                control={control}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                name={'translation_pricing_plan' as any}
                render={({ field: { onChange, value } }) => {
                  const numericValue =
                    typeof value === 'object' && value !== null
                      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (value as any).id
                      : value != null && value !== ''
                        ? Number(value)
                        : null;
                  const all = pricePlans?.result ?? [];
                  const translationPlans = all.filter(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (p: any) => p.type === 'TRANSLATION'
                  );
                  const selectedPlan =
                    translationPlans.find(p => p.id === numericValue) ||
                    all.find(p => p.id === numericValue);
                  return (
                    <SelectInput
                      id="translation_pricing_plan"
                      name="translation_pricing_plan"
                      options={translationPlans.map(p => ({
                        label: p.name,
                        value: p.id.toString()
                      }))}
                      onChange={selected => {
                        onChange(
                          selected
                            ? Number(
                                (selected as SingleValue<LabelValueSelectInput>)
                                  ?.value ?? 0
                              )
                            : null
                        );
                      }}
                      value={
                        numericValue != null
                          ? {
                              label:
                                selectedPlan?.name ?? `Plan #${numericValue}`,
                              value: String(numericValue)
                            }
                          : null
                      }
                      placeholder={t('select')}
                      isMulti={false}
                    />
                  );
                }}
              />
              <PricePlanOverview
                pricePlanId={
                  typeof selectedTranslationPlanId === 'object' &&
                  selectedTranslationPlanId !== null
                    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (selectedTranslationPlanId as any).id
                    : (selectedTranslationPlanId as number | null | undefined)
                }
                unitLabel="char"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Input
              id="translation_cost"
              type="number"
              label={t('cost-per-unit')}
              defaultValue={planBo?.result.translation_cost ?? ''}
              autoComplete="translation_cost"
              placeholder="0.0001"
              {...register('translation_cost', {
                required: t('cost-per-unit')
              })}
              onChange={e =>
                setValue('translation_cost', Number(e.target.value))
              }
              error={errors.translation_cost?.message}
            />
            <Input
              id="translation_characters"
              type="number"
              label={t('characters-per-unit')}
              defaultValue={planBo?.result.translation_characters ?? ''}
              autoComplete="translation_characters"
              placeholder="1000"
              {...register('translation_characters', {
                required: t('characters-per-unit')
              })}
              onChange={e =>
                setValue('translation_characters', Number(e.target.value))
              }
              error={errors.translation_characters?.message}
            />
            <Input
              id="translation_character_limit"
              type="number"
              label={t('translation-character-limit')}
              defaultValue={planBo?.result.translation_character_limit ?? ''}
              autoComplete="translation_character_limit"
              placeholder={t('optional')}
              {...register('translation_character_limit')}
              onChange={e =>
                setValue(
                  'translation_character_limit',
                  e.target.value ? Number(e.target.value) : null
                )
              }
              error={errors.translation_character_limit?.message}
            />
          </div>
        </section>
      )}

      {/* Training Pricing */}
      {isAutomaticTrainingEnabled && (
        <section
          className="rounded-2xl border shadow-sm p-4 sm:p-5"
          style={sectionStyle}
        >
          <div className="mb-4">
            <h3 className="text-sm font-semibold tracking-wide">
              {t('training-pricing')}
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Input
              id="automatic_training_unit_cost"
              type="number"
              label={t('unit-cost')}
              defaultValue={planBo?.result.automatic_training_unit_cost ?? ''}
              autoComplete="automatic_training_unit_cost"
              placeholder="0.00"
              {...register('automatic_training_unit_cost', {
                required: t('unit-cost')
              })}
              onChange={e =>
                setValue('automatic_training_unit_cost', Number(e.target.value))
              }
              error={errors.automatic_training_unit_cost?.message}
            />
            <Input
              id="automatic_training_monthly_cost"
              type="number"
              label={t('monthly-cost')}
              defaultValue={
                planBo?.result.automatic_training_monthly_cost ?? ''
              }
              autoComplete="automatic_training_monthly_cost"
              placeholder="0.00"
              {...register('automatic_training_monthly_cost', {
                required: t('monthly-cost')
              })}
              onChange={e =>
                setValue(
                  'automatic_training_monthly_cost',
                  Number(e.target.value)
                )
              }
              error={errors.automatic_training_monthly_cost?.message}
            />
          </div>
        </section>
      )}

      {/* Template Pricing */}
      {isTemplateEnabled && (
        <section
          className="rounded-2xl border shadow-sm p-4 sm:p-5"
          style={sectionStyle}
        >
          <div className="mb-4">
            <h3 className="text-sm font-semibold tracking-wide">
              {t('template-pricing')}
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Input
              id="stt_templates_exported_cost"
              type="number"
              autoComplete="templates-cost"
              label={t('export-cost')}
              placeholder="0.00"
              defaultValue={planBo?.result.stt_templates_exported_cost ?? ''}
              {...register('stt_templates_exported_cost')}
              onChange={e =>
                setValue('stt_templates_exported_cost', Number(e.target.value))
              }
              error={errors.stt_templates_exported_cost?.message}
            />
          </div>
        </section>
      )}

      {/* Fair Use / Usage Limits */}
      <section
        className="rounded-2xl border shadow-sm p-4 sm:p-5"
        style={sectionStyle}
      >
        <div className="mb-3">
          <h3 className="text-sm font-semibold tracking-wide">
            {t('usage-limits')}
          </h3>
          <p className="mt-1 text-xs opacity-80">{t('fair-use-description')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => {
              const newValue = !isFairUseEnabled;
              setFairUseEnabled(newValue);
              setValue('is_fair_use', newValue);
            }}
            className="relative p-3 rounded-2xl border-2 text-left transition-all"
            style={{
              borderColor: isFairUseEnabled
                ? 'rgba(20, 184, 166, 0.7)'
                : getColour('border'),

              backgroundColor: isFairUseEnabled
                ? 'rgba(20, 184, 166, 0.08)'
                : getColour('secondLayer')
            }}
          >
            {isFairUseEnabled && (
              <div
                className="absolute top-2 right-2 h-4 w-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgba(20, 184, 166, 0.9)' }}
              >
                <Check className="h-2.5 w-2.5 text-white" />
              </div>
            )}
            <div
              className="h-7 w-7 rounded-2xl flex items-center justify-center mb-2"
              style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)' }}
            >
              {}
              <Shield
                className="h-3.5 w-3.5"
                style={{ color: 'rgba(20, 184, 166, 0.9)' }}
              />
            </div>
            <span
              className="text-xs font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('enable-fair-use-mode')}
            </span>
            <span
              className="text-[10px] block mt-0.5"
              style={{ color: getColour('inactiveText') }}
            >
              {t('fair-use-mode-description')}
            </span>
          </button>

          <div
            className="md:col-span-2 p-3 rounded-2xl text-xs space-y-1 flex flex-col justify-center"
            style={{
              backgroundColor: getColour('baseLayer'),
              color: getColour('inactiveText')
            }}
          >
            <p>
              <span
                className="font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('when-enabled')}:
              </span>{' '}
              {t('fair-use-soft-limit-explanation')}
            </p>
            <p>
              <span
                className="font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('when-disabled')}:
              </span>{' '}
              {t('fair-use-hard-limit-explanation')}
            </p>
          </div>
        </div>

        {isFairUseEnabled && (
          <div
            className="mt-3 p-3 rounded-2xl"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Input
                  id="fair_use_rate_limit"
                  type="number"
                  label={
                    <>
                      {t('rate-limit')}
                      <span
                        className="ml-1 text-[10px] font-normal"
                        style={{ color: getColour('inactiveText') }}
                      >
                        ({t('requests-per-minute')})
                      </span>
                    </>
                  }
                  defaultValue={planBo?.result?.fair_use_rate_limit ?? ''}
                  placeholder={t('unlimited')}
                  {...register('fair_use_rate_limit')}
                  onChange={e =>
                    setValue(
                      'fair_use_rate_limit',
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                />
                <p
                  className="text-[10px] mt-1"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('rate-limit-note')}
                </p>
              </div>
              <div>
                <Input
                  id="automatic_training_unit_limit"
                  type="number"
                  label={
                    <>
                      {t('training-limit')}
                      <span
                        className="ml-1 text-[10px] font-normal"
                        style={{ color: getColour('inactiveText') }}
                      >
                        ({t('per-period')})
                      </span>
                    </>
                  }
                  defaultValue={
                    planBo?.result?.automatic_training_unit_limit ?? ''
                  }
                  placeholder={t('unlimited')}
                  {...register('automatic_training_unit_limit')}
                  onChange={e =>
                    setValue(
                      'automatic_training_unit_limit',
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                />
              </div>
              <div>
                <Input
                  id="automatic_training_hosting_limit"
                  type="number"
                  label={
                    <>
                      {t('hosting-limit')}
                      <span
                        className="ml-1 text-[10px] font-normal"
                        style={{ color: getColour('inactiveText') }}
                      >
                        ({t('models')})
                      </span>
                    </>
                  }
                  defaultValue={
                    planBo?.result?.automatic_training_hosting_limit ?? ''
                  }
                  placeholder={t('unlimited')}
                  {...register('automatic_training_hosting_limit')}
                  onChange={e =>
                    setValue(
                      'automatic_training_hosting_limit',
                      e.target.value ? Number(e.target.value) : undefined
                    )
                  }
                />
              </div>
            </div>
          </div>
        )}

        <div className="mt-4">
          <Input
            id="custom_model_yearly_limit"
            type="number"
            label={
              <>
                {t('custom-models-per-year')}
                <span
                  className="ml-1 text-[10px] font-normal"
                  style={{ color: getColour('inactiveText') }}
                >
                  ({t('per-year')})
                </span>
              </>
            }
            defaultValue={planBo?.result?.custom_model_yearly_limit ?? ''}
            placeholder={t('unlimited')}
            {...register('custom_model_yearly_limit')}
            onChange={e =>
              setValue(
                'custom_model_yearly_limit',
                e.target.value ? Number(e.target.value) : undefined
              )
            }
          />
          <p
            className="text-[10px] mt-1"
            style={{ color: getColour('inactiveText') }}
          >
            {t('custom-models-per-year-description')}
          </p>
        </div>
      </section>

      {/* Exceedance Email Alerts */}
      <section
        className="rounded-2xl border shadow-sm p-4 sm:p-5"
        style={sectionStyle}
      >
        <div className="mb-4">
          <h3 className="text-sm font-semibold tracking-wide">
            {t('exceedance-email-alerts')}
          </h3>
          <p className="mt-1 text-xs opacity-80">
            {t('exceedance-email-alerts-description')}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded-2xl border-hairline"
              {...register('send_exceedance_email')}
              onChange={e => {
                setValue('send_exceedance_email', e.target.checked);
                if (!e.target.checked)
                  setValue('exceedance_email_recipients', '');
              }}
            />
            <span className="text-sm font-medium">
              {t('send-exceedance-email')}
            </span>
          </label>
          {sendExceedanceEmail && (
            <Input
              id="exceedance_email_recipients"
              type="text"
              label={t('exceedance-email-recipients')}
              defaultValue={planBo?.result.exceedance_email_recipients ?? ''}
              autoComplete="exceedance_email_recipients"
              placeholder="email1@example.com, email2@example.com"
              {...register('exceedance_email_recipients', {
                required: sendExceedanceEmail
                  ? t('exceedance-email-recipients-required')
                  : false
              })}
              error={errors.exceedance_email_recipients?.message}
            />
          )}
        </div>
      </section>
    </div>
  );
}
